<template>
  <div class="research">
    <router-link
      class="link-back"
      :to="`/research/test/${this.$route.params.id}/participants`"
    >
      <div>&larr; Back to participants list</div>
    </router-link>
    <h1 v-bg-typography="'h1'" :style="{ marginTop: '20px' }">
      Participant {{ this.$route.params.participantId }}
    </h1>

    <div class="participant-tabs">
      <h2
        :class="this.activeTab === 'data' ? 'active' : ''"
        @click="changeTab('data')"
      >
        Data
      </h2>
      <h2
        v-if="testPlatform === 'video'"
        :class="this.activeTab === 'questionnaire' ? 'active' : ''"
        @click="changeTab('questionnaire')"
      >
        Questionnaire
      </h2>
      <h2
        :class="this.activeTab === 'sections' ? 'active' : ''"
        @click="changeTab('sections')"
        v-if="this.testPlatform != 'video'"
      >
        Sections
      </h2>
    </div>

    <bg-loader :dark="true" v-if="isLoading"></bg-loader>

    <div
      class="research-participant-content"
      v-if="!isLoading && data && dropdownValue"
    >
      <div class="grid" v-if="activeTab === 'data'">
        <div>
          <div class="header-stats">
            <div class="stat-col stat-col--nav">
              <bg-select
                @change="handleSelectChange"
                :options="participants"
                :value="dropdownValue"
              ></bg-select>
            </div>
            <div class="button" id="delete" @click="openDeleteModal">
              <bg-icon name="fdelete"></bg-icon>
            </div>
            <div class="stat-col">
              <div class="stat-header">Time to complete</div>
              <div class="stat-value stat-value--inline">
                <span>{{ timeToComplete() }}</span>
                <bg-tooltip
                  v-if="hasTtcMismatch"
                  :dark="true"
                  :info="true"
                  placement="top"
                >
                  <template #content>
                    Recording length can differ from time to complete because
                    video and mouse data are captured by separate processes.
                    Minor differences are expected.
                  </template>
                </bg-tooltip>
              </div>
            </div>
            <div class="stat-col">
              <div class="stat-header">Age</div>
              <div class="stat-value">{{ data.demographic.age }}</div>
            </div>
            <div class="stat-col">
              <div class="stat-header">Education</div>
              <div class="stat-value stat-value--upper">
                {{ data.demographic.education }}
              </div>
            </div>
            <div class="stat-col">
              <div class="stat-header">Gender</div>
              <div class="stat-value">{{ data.demographic.gender }}</div>
            </div>
            <bg-file-upload
              :uploaded="isVideoUploaded"
              :filename="data.video"
              :handleFileChange="handleFile"
              buttonText="Upload video"
              buttonReuploadText="Upload new video"
              :isLoading="fileUploading"
              :showFileName="false"
            />
            <bg-button
              class="export-button"
              text="Export data"
              postIcon="download"
              @click="openExportModal"
            />
          </div>

          <bg-video-chart
            v-if="!isLoading"
            :data="data"
            :customControls="true"
            :isNeurofeedbackEditable="true"
            :tags="tags"
            :testId="testId"
            :testPlatform="testPlatform"
            :insights="insights"
            :videoChartKey="videoChartKey"
            @videoLoaded="onVideoLoaded"
            @change="assignInsight"
            @pointDeleted="onPointDeleted"
          ></bg-video-chart>

          <bg-modal
            v-if="isModalOpen"
            body="Are you sure you want to remove this participant?"
            @close="isModalOpen = false"
          >
            <bg-loader :dark="true" v-if="isDeleting"></bg-loader>
            <bg-button
              variant="secondary"
              text="Cancel"
              @click="isModalOpen = false"
            />
            <bg-button
              variant="destructive"
              class="action"
              text="Delete"
              @click="deleteParticipant"
            />
          </bg-modal>

          <bg-modal
            v-if="isExportModalOpen"
            header="Export data"
            @close="closeExportModal"
          >
            <template #body>
              <p class="export-intro">Select the data to include.</p>
              <div class="export-options">
                <bg-checkbox
                  id="export-mouse"
                  label="Mouse"
                  :outline="true"
                  v-model="exportOptions.mouse"
                />
                <bg-checkbox
                  id="export-eye"
                  label="Eye tracking"
                  :outline="true"
                  v-model="exportOptions.eye"
                />
                <bg-checkbox
                  id="export-eeg"
                  label="Emotions"
                  :outline="true"
                  v-model="exportOptions.eeg"
                />
                <bg-checkbox
                  id="export-neurofeedback"
                  label="Neurofeedback"
                  :outline="true"
                  v-model="exportOptions.neurofeedback"
                />
                <bg-checkbox
                  id="export-video"
                  :label="hasVideo ? 'Video' : 'Video (no video available)'"
                  :outline="true"
                  :disabled="!hasVideo"
                  v-model="exportOptions.video"
                />
              </div>
              <p v-if="exportError" class="export-error">{{ exportError }}</p>
            </template>
            <bg-button
              variant="secondary"
              text="Cancel"
              :disabled="isExporting"
              @click="closeExportModal"
            />
            <bg-button
              class="action"
              text="Export"
              postIcon="download"
              :disabled="!canExport"
              :isLoading="isExporting"
              @click="exportData"
            />
          </bg-modal>
        </div>
        <div style="margin-top: 70px" v-if="this.testPlatform != 'video'">
          <h2 v-bg-typography="'h2'">Insights</h2>
          <insights-analysis
            ref="insights"
            :isLoading="isLoading"
            :selectFeedback="selectFeedback"
            style="margin-bottom: 70px 0px"
            @change="updateInsights"
          />
        </div>
      </div>
      <div v-if="activeTab === 'questionnaire'">
        <questionnaire :participant-data="data"></questionnaire>
      </div>
      <div v-if="activeTab === 'sections'">
        <sections
          :dropdownOptions="participants"
          :handleSelectChange="handleSelectChange"
          :dropdownValue="dropdownValue"
          :data="data"
          :testId="testId"
          :test-platform="testPlatform"
          :test-internal-state="testInternalState"
        ></sections>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "./styles.scss";

#delete {
  background-color: #ff4fbe;
  border: none;
}
#delete:hover {
  background-color: #ff14a9;
  border: none;
}

.button {
  background-color: white;
  border: 1px solid white;
  width: 45px;
  height: 45px;
  border-radius: 8px;
  margin-right: 35px;
  margin-bottom: 10px;
  justify-content: center;
  position: relative;
  display: inline-block;
}

.button img,
svg {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.export-button {
  margin-left: 1rem;
}

.export-intro {
  margin-bottom: 20px;
}

.export-options {
  display: flex;
  flex-direction: column;
}

.export-error {
  color: var(--bg-color-error);
  margin-top: 10px;
}
</style>

<script>
import moment from "moment";
import BgTypography from "@/directives/bg-typography";
import BgLoader from "@/components/bg-loader";
import BgSelect from "@/components/bg-select";
import BgButton from "@/components/bg-button";
import BgCheckbox from "@/components/bg-checkbox";
import BgFileUpload from "@/components/bg-file-upload";

import API from "@/api";
import { mapState } from "vuex";

import BgVideoChart from "@/components/bg-video-chart";
import BgModal from "@/components/bg-modal";
import Sections from "@/routes/dashboard/research/research-participants/research-participant/sections";
import Questionnaire from "@/routes/dashboard/research/research-participants/research-participant/questionnaire";

import InsightsAnalysis from "@/components/insights-analysis";
import BgIcon from "@/components/bg-icon";

export default {
  name: "researchParticipant",
  components: {
    BgButton,
    BgCheckbox,
    BgSelect,
    BgLoader,
    BgVideoChart,
    BgModal,
    InsightsAnalysis,
    BgIcon,
    Sections,
    Questionnaire,
    BgFileUpload,
    BgTooltip: () => import("@/components/bg-tooltip"),
  },
  directives: {
    BgTypography,
  },
  computed: {
    ...mapState({
      testState: (state) => state.test,
    }),
    hasVideo() {
      return !!(
        this.data &&
        this.data.video &&
        !this.data.video.includes("/videos/fallback/")
      );
    },
    selectedExportKinds() {
      return Object.keys(this.exportOptions).filter(
        (kind) => this.exportOptions[kind],
      );
    },
    canExport() {
      return this.selectedExportKinds.length > 0 && !this.isExporting;
    },
  },

  watch: {
    $route(to, from) {
      if (to.params.participantId !== from.params.participantId) {
        this.isExportModalOpen = false;
        this.getTest();
      }
    },
  },
  methods: {
    onVideoLoaded(duration) {
      this.loadedVideoDuration = duration;
      const ttcSeconds = this.computeTtcSeconds();
      if (ttcSeconds != null && duration != null) {
        this.hasTtcMismatch =
          Math.abs(Math.round(duration) - Math.round(ttcSeconds)) >= 1;
      } else {
        this.hasTtcMismatch = false;
      }
    },
    handleFile(file) {
      let formData = new FormData();
      formData.append("video", file);
      this.isVideoUploaded = false;
      this.fileUploading = true;
      API.test
        .updateParticipantWithFiles(
          this.$route.params.id,
          this.$route.params.participantId,
          formData,
        )
        .then((res) => {
          this.videoChartKey += 1;
          this.isVideoUploaded = true;
          this.fileUploading = false;
          this.data = res;
        });
    },
    assignInsight(val) {
      const child = this.$refs.insights;
      child.addSP(val);
    },
    onPointDeleted({ insightsCount }) {
      const child = this.$refs.insights;
      if (insightsCount > 0 && child) child.fetchData();
    },
    getParticipant() {
      this.hasTtcMismatch = false;
      this.loadedVideoDuration = null;
      API.test
        .getParticipant(this.$route.params.id, this.$route.params.participantId)
        .then((res) => {
          this.data = res;
        });
    },
    openDeleteModal() {
      this.isModalOpen = true;
    },
    async deleteParticipant() {
      this.isDeleting = true;
      this.$route.params.participantId;
      API.test
        .deleteParticipant(
          this.$route.params.id,
          this.$route.params.participantId,
        )
        .then((res) => {
          this.data = res;
          this.isModalOpen = false;
          this.$router.push({
            path: `/research/test/${this.$route.params.id}/participants`,
          });
        });
    },
    openExportModal() {
      this.exportError = null;
      this.exportOptions = {
        mouse: true,
        eye: true,
        eeg: true,
        neurofeedback: true,
        video: this.hasVideo,
      };
      this.isExportModalOpen = true;
    },
    closeExportModal() {
      if (this.isExporting) {
        return;
      }
      this.isExportModalOpen = false;
      this.exportError = null;
    },
    async exportData() {
      if (!this.canExport) {
        return;
      }
      this.isExporting = true;
      this.exportError = null;
      try {
        const res = await API.test.createParticipantExport(
          this.$route.params.id,
          this.$route.params.participantId,
          this.selectedExportKinds,
        );
        const url =
          res.url ||
          API.test.participantExportUrl(
            this.$route.params.id,
            this.$route.params.participantId,
            res.token,
          );
        // The response is served with Content-Disposition: attachment, so the
        // browser downloads the zip and the app stays loaded.
        window.location.assign(url);
        this.isExportModalOpen = false;
      } catch (err) {
        const errors =
          err && err.response && err.response.data && err.response.data.errors;
        this.exportError = errors
          ? [].concat(...Object.values(errors)).join(" ")
          : "The export could not be started. Please try again.";
      } finally {
        this.isExporting = false;
      }
    },
    handleSelectChange(val) {
      if (this.$route.participantId !== val) {
        this.$router.replace(
          `/research/test/${this.$route.params.id}/participants/${val.value}`,
        );
      }
    },
    selectFeedback(val) {
      const routeObj = {
        path: `/test/${this.$route.params.id}/participant/${val.participant_id}/recording`,
        query: {
          timestamp: val.timestamp_top,
          sp_id: val.id,
        },
      };
      let routeData = this.$router.resolve(routeObj);
      window.open(routeData.href, "_blank");
    },
    timeToComplete() {
      if (this.data) {
        const startTime = moment(this.data.started_at);
        const endTime = moment(this.data.ended_at);
        const diff = endTime.diff(startTime);
        return moment(diff).format("mm:ss");
      }
    },
    computeTtcSeconds() {
      if (!this.data) return null;
      const startTime = moment(this.data.started_at);
      const endTime = moment(this.data.ended_at);
      return endTime.diff(startTime, "seconds", true);
    },
    updateInsights(val) {
      this.insights = val;
    },
    changeTab(tabName) {
      this.activeTab = tabName;
    },
  },
  data() {
    return {
      data: null,
      participants: [],
      insights: [],
      isLoading: true,
      isDeleting: false,
      isModalOpen: false,
      isExportModalOpen: false,
      isExporting: false,
      exportError: null,
      exportOptions: {
        mouse: true,
        eye: true,
        eeg: true,
        neurofeedback: true,
        video: true,
      },
      dropdownValue: false,
      tags: [],
      testId: null,
      activeTab: "data",
      testPlatform: null,
      testInternalState: null,
      isVideoUploaded: true,
      videoChartKey: 1000,
      fileUploading: false,
      loadedVideoDuration: null,
      hasTtcMismatch: false,
    };
  },
  mounted() {
    this.getParticipant();
    API.test.getTest(this.$route.params.id).then((res) => {
      this.tags = res.tags.map((tag) => {
        return { name: tag.name, value: tag.id };
      });
      this.testId = res.id;
      this.testInternalState = res.internal_state;
      this.testPlatform = res.platform;
      this.participants = [];
      res.participants.forEach((p) => {
        if (p.type === "default") {
          this.participants.push({
            value: p.id,
            label: `Participant ${p.id}`,
          });
        }
      });

      this.dropdownValue = {
        value: this.$route.params.participantId,
        label: `Participant ${this.$route.params.participantId}`,
      };

      this.isLoading = false;
    });
  },
};
</script>

<style scoped>
.stat-value--inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
</style>
